import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { dealsAPI, documentsAPI, paymentsAPI, uploadLinksAPI, esignAPI, docTemplatesAPI, docSignAPI, inventoryAPI, lendersAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, DollarSign, Send, Download, Eye, Plus, PenTool,
  Upload, Link as LinkIcon, Clock, Pencil, MessageSquare, Trash2, CreditCard,
  CheckCircle, XCircle, Copy, Files, Shield, Sparkles, Loader2, Truck, Search, RefreshCw, ChevronDown, ChevronUp, Printer
} from 'lucide-react';
import DocGenerateDialog from '@/components/DocGenerateDialog';
import LenderPackagePreview from '@/components/LenderPackagePreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stageLabels = {
  lead: 'Lead', qualified: 'Qualified', quoted: 'Quoted', negotiation: 'Negotiation',
  docs_pending: 'Docs Pending', submitted: 'Submitted', approved: 'Approved',
  deposit: 'Deposit', financing: 'Financing', funded: 'Funded',
  closed_won: 'Closed Won', closed_lost: 'Closed Lost',
};
const stageColors = {
  lead: 'bg-gray-100 text-gray-700 border border-gray-300', qualified: 'bg-blue-100 text-blue-800 border border-blue-300',
  quoted: 'bg-indigo-100 text-indigo-800 border border-indigo-300', negotiation: 'bg-orange-100 text-orange-800 border border-orange-300',
  docs_pending: 'bg-amber-100 text-amber-800 border border-amber-300', submitted: 'bg-purple-100 text-purple-800 border border-purple-300',
  approved: 'bg-green-100 text-green-800 border border-green-300', deposit: 'bg-teal-100 text-teal-800 border border-teal-300',
  financing: 'bg-cyan-100 text-cyan-800 border border-cyan-300', funded: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
  closed_won: 'bg-green-200 text-green-900 border border-green-400', closed_lost: 'bg-red-100 text-red-800 border border-red-300',
};

export default function DealDetail() {
  const { clientId, dealId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lenderPkgPreviewOpen, setLenderPkgPreviewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [docDialog, setDocDialog] = useState(false);
  const [docForm, setDocForm] = useState({ type: 'pre_approval', items: [{ description: '', amount: '' }], notes: '', subtotal: '', tax: '', total: '', deposit_amount: '', invoice_number: '' });
  const [staffList, setStaffList] = useState([]);
  const [holdDepositOpen, setHoldDepositOpen] = useState(false);
  const [holdAmount, setHoldAmount] = useState('');
  const [holdNotes, setHoldNotes] = useState('');
  const [sendingDeposit, setSendingDeposit] = useState(false);
  const [esignOpen, setEsignOpen] = useState(false);
  const [sendingEsign, setSendingEsign] = useState(false);
  const [esignFields, setEsignFields] = useState({});
  const [esignTemplate, setEsignTemplate] = useState(null);
  const [esignStep, setEsignStep] = useState('edit'); // 'edit' | 'preview'
  const [docSignOpen, setDocSignOpen] = useState(false);
  const [docTemplates, setDocTemplates] = useState([]);
  const [selectedDocTemplate, setSelectedDocTemplate] = useState('');
  const [sendingDocSign, setSendingDocSign] = useState(false);
  const [docSignRequests, setDocSignRequests] = useState([]);
  const [submittingCredit, setSubmittingCredit] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  // Broker Notes state
  const [brokerNotes, setBrokerNotes] = useState('');
  const [staffBrokerInput, setStaffBrokerInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  // Equipment picker state
  const [equipOpen, setEquipOpen] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [invSearch, setInvSearch] = useState('');
  const [loadingInv, setLoadingInv] = useState(false);
  const [customEquipOpen, setCustomEquipOpen] = useState(false);
  const [customEquip, setCustomEquip] = useState({ year: '', make: '', model: '', price: '', notes: '' });
  // Lightbox state
  const [lightbox, setLightbox] = useState({ open: false, url: '', filename: '', type: '' });
  // View All Equipment Modal
  const [viewAllEquipOpen, setViewAllEquipOpen] = useState(false);
  const [addEquipMode, setAddEquipMode] = useState(false);
  const [addEquipSearch, setAddEquipSearch] = useState('');
  const [savingEquip, setSavingEquip] = useState(false);
  // Edit Unit Modal
  const [editUnitOpen, setEditUnitOpen] = useState(false);
  const [editUnitData, setEditUnitData] = useState(null);
  const [editUnitMode, setEditUnitMode] = useState('edit'); // 'edit' | 'swap'
  const [swapSearch, setSwapSearch] = useState('');
  const [priceOverride, setPriceOverride] = useState('');
  // Activity log
  const [activityLog, setActivityLog] = useState([]);
  const [activityOpen, setActivityOpen] = useState(false);
  const [docGenOpen, setDocGenOpen] = useState(false);
  const [lenders, setLenders] = useState([]);
  // Send Document Email state
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [sendEmailDoc, setSendEmailDoc] = useState(null);
  const [sendEmailMode, setSendEmailMode] = useState('client');  // 'client', 'lender', 'custom'
  const [sendEmailCustom, setSendEmailCustom] = useState('');
  const [sendEmailLenderId, setSendEmailLenderId] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  // Document category upload state
  const [docUploadOpen, setDocUploadOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [editingCatId, setEditingCatId] = useState(null);
  const DOC_CATEGORIES = ['Bank Statements', 'Credit App', 'Articles', 'Driving Licence', 'Financials', 'T2', 'Ownership Copy', 'POA', 'Bill of Sale', 'Invoice', 'Safety Certificate', 'Hold Deposit', 'Other'];

  const load = useCallback(async () => {
    try {
      const res = await dealsAPI.get(dealId);
      setDeal(res.data);
      setBrokerNotes(res.data?.broker_notes || '');
      // Load doc sign requests
      try { const ds = await docSignAPI.getDealRequests(dealId); setDocSignRequests(ds.data || []); } catch (e) { console.error('Doc sign load error:', e); }
      // Load activity log
      try { const al = await dealsAPI.getActivity(dealId); setActivityLog(al.data || []); } catch (e) { console.error('Activity load error:', e); }
    } catch { toast.error('Failed to load deal'); }
    setLoading(false);
  }, [dealId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { lendersAPI.list().then(r => setLenders(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get('/staff-list').then(r => setStaffList(r.data || [])).catch(() => setStaffList([])); }, []);

  const openEdit = () => {
    setEditForm({
      title: deal?.title || '',
      stage: deal?.stage || 'lead',
      amount: deal?.amount?.toString() || '',
      notes: deal?.notes || '',
      financing_amount: deal?.financing_amount?.toString() || '',
      monthly_payment: deal?.monthly_payment?.toString() || '',
      term: deal?.term?.toString() || '',
      interest_rate: deal?.interest_rate?.toString() || '',
      downpayment: deal?.downpayment?.toString() || '',
      funding_date: deal?.funding_date || '',
    });
    setEditOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editForm };
      ['amount', 'financing_amount', 'monthly_payment', 'downpayment', 'interest_rate'].forEach(k => {
        payload[k] = payload[k] ? parseFloat(payload[k]) : null;
      });
      payload.term = payload.term ? parseInt(payload.term) : null;
      await dealsAPI.update(dealId, payload);
      toast.success('Deal updated');
      setEditOpen(false);
      load();
    } catch { toast.error('Failed to update'); }
    setSaving(false);
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await dealsAPI.addNote(dealId, { content: noteText.trim() });
      toast.success('Note added');
      setNoteText('');
      load();
    } catch { toast.error('Failed to add note'); }
    setAddingNote(false);
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await dealsAPI.deleteNote(dealId, noteId);
      toast.success('Note deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  // Document generation
  const addDocItem = () => setDocForm(f => ({ ...f, items: [...f.items, { description: '', amount: '' }] }));
  const updateDocItem = (idx, key, val) => {
    setDocForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [key]: val };
      const sub = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
      const tax = sub * 0.13;
      return { ...f, items, subtotal: sub.toFixed(2), tax: tax.toFixed(2), total: (sub + tax).toFixed(2) };
    });
  };

  const handleGenerateDoc = async () => {
    setSaving(true);
    try {
      if (docForm.type === 'bill_of_sale') {
        // Client-side BOS invoice-number validation:
        // - prefer the user-supplied invoice_number
        // - fall back to the deal's sticky invoice_number
        // - else need a VIN on at least one selected unit (backend will auto-compute CTS-{last4VIN})
        // - else block with a clear message
        const unitIds = (deal?.inventory_ids?.length ? deal.inventory_ids : [deal?.inventory_id]).filter(Boolean);
        const firstUnit = (deal?.units || []).find(u => u.id === unitIds[0]) || null;
        const hasVin = !!(firstUnit && (firstUnit.vin || '').trim().length >= 4);
        const stickyInvoice = (deal?.invoice_number || '').trim();
        if (!docForm.invoice_number?.trim() && !stickyInvoice && !hasVin) {
          toast.error('Invoice Number is required — no equipment VIN found on this deal.');
          setSaving(false);
          return;
        }
        // Use the new CTS BOS endpoint
        const bosPayload = {
          invoice_number: (docForm.invoice_number || '').trim() || undefined,
          doc_fee: parseFloat(docForm.doc_fee) || 0,
          licence_fee: parseFloat(docForm.licence_fee) || 0,
          trade_in_amount: parseFloat(docForm.trade_in_amount) || 0,
          deposit_amount: parseFloat(docForm.deposit_amount) || 0,
          custom_price: parseFloat(docForm.custom_price) || 0,
          payment_method: docForm.payment_method || 'Certified Funds',
          sale_condition: docForm.sale_condition || 'as_is',
          notes: docForm.notes || '',
          for_lender: docForm.for_lender || false,
          lender_id: docForm.lender_id || '',
          ship_to_same: docForm.for_lender ? false : docForm.ship_to_same !== false,
          ship_to_name: docForm.ship_to_name || '',
          ship_to_address: docForm.ship_to_address || '',
          ship_to_city: docForm.ship_to_city || '',
          ship_to_phone: docForm.ship_to_phone || '',
          equipment_ids: deal?.inventory_ids || [],
          pricing_mode: docForm.pricing_mode || 'listing',
          unit_prices: Object.fromEntries(
            Object.entries(docForm.unit_prices || {}).map(([k, v]) => [k, parseFloat(v) || 0]).filter(([, v]) => v > 0)
          ),
        };
        const res = await dealsAPI.generateBos(dealId, bosPayload);
        toast.success(`Bill of Sale ${res.data.invoice_number} generated!`);
        // Prefer branded download URL (clean filename, no raw file path); fall back to preview URL.
        const openUrl = res.data.download_url || res.data.url;
        if (openUrl) window.open(`${API_URL}${openUrl}`, '_blank');
        setDocDialog(false);
        load();
      } else {
        const payload = {
          ...docForm,
          client_id: deal?.client_id,
          deal_id: dealId,
          inventory_id: deal?.inventory_id || '',
          items: docForm.items.map(i => ({ description: i.description, amount: parseFloat(i.amount) || 0 })),
          subtotal: parseFloat(docForm.subtotal) || undefined,
          tax: parseFloat(docForm.tax) || undefined,
          total: parseFloat(docForm.total) || undefined,
          deposit_amount: parseFloat(docForm.deposit_amount) || undefined,
        };
        const res = await documentsAPI.generate(payload);
        toast.success(`${docForm.type.replace(/_/g, ' ')} generated!`);
        if (res.data.url) window.open(`${API_URL}${res.data.url}`, '_blank');
        setDocDialog(false);
        load();
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Generation failed';
      toast.error(typeof msg === 'string' ? msg : 'Generation failed');
      console.error('[BOS] Generate failed:', err);
    }
    setSaving(false);
  };

  const handleSendPreApproval = async () => {
    setSaving(true);
    try {
      const res = await uploadLinksAPI.sendPreApproval(deal?.client_id, dealId, '');
      if (res.data?.email_sent) {
        toast.success('Pre-approval request sent to client!');
        setDocDialog(false);
        load();
      } else {
        toast.error(res.data?.email_error || 'Failed to send email');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send pre-approval request');
    }
    setSaving(false);
  };

  const handleSendHoldDeposit = async () => {
    if (!holdAmount || parseFloat(holdAmount) <= 0) { toast.error('Enter valid amount'); return; }
    setSendingDeposit(true);
    try {
      const res = await paymentsAPI.createHoldDepositLink({
        deal_id: dealId,
        client_id: deal?.client_id,
        inventory_id: deal?.inventory_id,
        amount: parseFloat(holdAmount),
        notes: holdNotes,
        description: `Hold Deposit - ${deal?.unit?.title || deal?.title || 'Unit'}`,
      });
      toast.success(res.data.message);
      setHoldDepositOpen(false);
      setHoldAmount('');
      setHoldNotes('');
      load();
    } catch { toast.error('Failed to create deposit link'); }
    setSendingDeposit(false);
  };

  const openEsignDialog = async () => {
    try {
      const res = await esignAPI.getTemplate();
      const tmpl = res.data;
      setEsignTemplate(tmpl);
      // Prefill fields from client data
      const prefilled = {};
      const clientData = deal?.client || {};
      const prefillMap = { name: clientData.name || '', email: clientData.email || '', phone: clientData.phone || '', company: clientData.company || '' };
      (tmpl.fields || []).forEach(f => {
        if (f.prefill_key && prefillMap[f.prefill_key]) {
          prefilled[f.id] = prefillMap[f.prefill_key];
        } else {
          prefilled[f.id] = '';
        }
      });
      setEsignFields(prefilled);
      setEsignStep('edit');
      setEsignOpen(true);
    } catch { toast.error('Failed to load template'); }
  };

  const handleSendEsign = async () => {
    setSendingEsign(true);
    try {
      const res = await esignAPI.create({
        deal_id: dealId,
        client_id: deal?.client_id,
        document_type: 'credit_application',
        signer_name: esignFields.full_name || deal?.client?.name,
        signer_email: esignFields.email || deal?.client?.email,
        prefilled_fields: esignFields,
      });
      const link = `${window.location.origin}${res.data.link}`;
      navigator.clipboard.writeText(link);
      toast.success('Credit app link created & copied to clipboard!');
      setEsignOpen(false);
      load();
    } catch { toast.error('Failed to create credit app'); }
    setSendingEsign(false);
  };

  const handleSendUploadLink = async () => {
    try {
      const res = await uploadLinksAPI.generate(deal?.client_id, dealId);
      const link = `${window.location.origin}/upload/${res.data.token}`;
      navigator.clipboard.writeText(link);
      if (res.data.email_sent) {
        toast.success(`Upload link emailed to ${deal?.client?.email || 'client'} & copied to clipboard`);
      } else {
        toast.success('Upload link copied to clipboard (email delivery failed)');
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send upload link'); }
  };

  const handleSubmitToCredit = async () => {
    if (!brokerNotes.trim()) {
      toast.error('Broker notes are required before submitting to credit');
      return;
    }
    setSubmittingCredit(true);
    try {
      await dealsAPI.submitToCredit({
        client_id: deal?.client_id,
        inventory_id: deal?.inventory_id || '',
        broker_notes: brokerNotes,
        amount: deal?.amount || 0,
        custom_equipment: deal?.custom_equipment || null,
      });
      toast.success('Submitted to credit successfully!');
      load(); // Refresh deal to update stage
    } catch (err) { toast.error(err.response?.data?.detail || 'Submit failed'); }
    setSubmittingCredit(false);
  };

  // Bill of Sale handlers
  const [sendingBillOfSale, setSendingBillOfSale] = useState(false);

  const handleViewBillOfSale = () => {
    const token = localStorage.getItem('cts_token');
    const url = dealsAPI.getBillOfSale(dealId);
    window.open(`${url}?token=${token}`, '_blank');
  };

  const handlePrintBillOfSale = async () => {
    const token = localStorage.getItem('cts_token');
    const url = dealsAPI.getBillOfSale(dealId);
    try {
      const resp = await fetch(`${url}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const printWindow = window.open(blobUrl, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => { printWindow.print(); });
      }
    } catch { toast.error('Failed to load Bill of Sale'); }
  };

  const handleSendBillOfSale = async () => {
    setSendingBillOfSale(true);
    try {
      const res = await dealsAPI.sendBillOfSale(dealId);
      toast.success(`Bill of Sale sent to ${res.data.email}`);
      loadDeal();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    setSendingBillOfSale(false);
  };


  const openDocSignDialog = async () => {
    try {
      const res = await docTemplatesAPI.list();
      setDocTemplates((res.data || []).filter(t => t.page_count > 0));
      setSelectedDocTemplate('');
      setDocSignOpen(true);
    } catch { toast.error('Failed to load templates'); }
  };

  const handleSendDocSign = async () => {
    if (!selectedDocTemplate) { toast.error('Select a template'); return; }
    setSendingDocSign(true);
    try {
      const clientData = deal?.client || {};
      const prefilled = {};
      const tmpl = docTemplates.find(t => t.id === selectedDocTemplate);
      (tmpl?.fields || []).forEach(f => {
        if (f.type === 'name') prefilled[f.id] = clientData.name || '';
        else if (f.type === 'email') prefilled[f.id] = clientData.email || '';
        else if (f.type === 'phone') prefilled[f.id] = clientData.phone || '';
      });
      const res = await docSignAPI.create({
        template_id: selectedDocTemplate,
        client_id: deal?.client_id,
        deal_id: dealId,
        signer_name: clientData.name,
        signer_email: clientData.email,
        prefilled_fields: prefilled,
      });
      const link = `${window.location.origin}${res.data.link}`;
      navigator.clipboard.writeText(link);
      toast.success('Signing link created & copied!');
      setDocSignOpen(false);
      load();
    } catch { toast.error('Failed'); }
    setSendingDocSign(false);
  };

  const handlePortfolioUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    // Open category picker dialog with files
    setPendingFiles(files.map(f => ({ file: f, category: '', customCategory: '' })));
    setDocUploadOpen(true);
    e.target.value = '';
  };

  const handleConfirmUpload = async () => {
    const uncategorized = pendingFiles.some(f => !f.category);
    if (uncategorized) { toast.error('Please select a category for all files'); return; }
    setUploadingDoc(true);
    setDocUploadOpen(false);
    let success = 0;
    for (const entry of pendingFiles) {
      try {
        const cat = entry.category === 'Other' ? (entry.customCategory || 'Other') : entry.category;
        await dealsAPI.uploadDoc(dealId, entry.file, cat);
        success++;
      } catch { /* continue with next file */ }
    }
    if (success > 0) {
      toast.success(`${success} file(s) uploaded`);
      load();
    }
    if (success < pendingFiles.length) toast.error(`${pendingFiles.length - success} file(s) failed`);
    setUploadingDoc(false);
    setPendingFiles([]);
  };

  const handleUpdateDocCategory = async (docId, newCat) => {
    try {
      await dealsAPI.updateDocCategory(dealId, docId, newCat);
      toast.success('Category updated');
      setEditingCatId(null);
      load();
    } catch { toast.error('Failed to update category'); }
  };

  const handleDeleteDealDoc = async (docId) => {
    if (!confirm('Delete this document?')) return;
    try {
      await dealsAPI.deleteDealDoc(dealId, docId);
      toast.success('Document deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDeleteGeneratedDoc = async (docId, label) => {
    if (!window.confirm(`Delete ${label || 'this document'}? This cannot be undone.`)) return;
    try {
      await documentsAPI.delete(docId);
      toast.success('Document deleted');
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to delete';
      toast.error(typeof msg === 'string' ? msg : 'Failed to delete');
      console.error('[DOC-DELETE] failed:', err);
    }
  };

  // AI Broker Notes
  const handleGenerateBrokerNotes = async () => {
    setAiLoading(true);
    try {
      const c = deal?.client || {};
      const u = deal?.unit || {};
      const ce = deal?.custom_equipment || {};

      // Rich client info
      const clientParts = [
        c.name && `Name: ${c.name}`,
        c.company && `Company: ${c.company}`,
        c.email && `Email: ${c.email}`,
        c.phone && `Phone: ${c.phone}`,
        c.address && `Address: ${c.address}`,
        c.city && `City: ${c.city}`,
        c.province && `Province: ${c.province}`,
        c.postal_code && `Postal Code: ${c.postal_code}`,
        c.drivers_licence && `Driver's Licence: ${c.drivers_licence}`,
        c.dob && `DOB: ${c.dob}`,
        c.sin && `SIN: ${c.sin}`,
        c.bank && `Bank: ${c.bank}`,
        c.salesperson && `Salesperson: ${c.salesperson}`,
      ].filter(Boolean).join('\n');

      // Rich equipment info
      const equipParts = u.id ? [
        `${u.year || ''} ${u.make || ''} ${u.model || ''} ${u.trim || ''}`.trim(),
        u.stock_number && `Stock #: ${u.stock_number}`,
        u.vin && `VIN: ${u.vin}`,
        u.mileage_km && `Kilometers: ${Number(u.mileage_km).toLocaleString()} km`,
        u.price && `Price: $${Number(u.price).toLocaleString()} CAD`,
        u.body_type && `Body Type: ${u.body_type}`,
        u.engine && `Engine: ${u.engine}`,
        u.transmission && `Transmission: ${u.transmission}`,
        u.condition && `Condition: ${u.condition}`,
        u.horsepower && `Horsepower: ${u.horsepower} HP`,
        u.axles && `Axles: ${u.axles}`,
      ].filter(Boolean).join('\n') : ce ? [
        `${ce.year || ''} ${ce.make || ''} ${ce.model || ''}`.trim(),
        ce.mileage && `Kilometers: ${ce.mileage} km`,
        ce.price && `Price: $${Number(ce.price).toLocaleString()} CAD`,
        deal?.is_private_sale && deal?.seller_name && `Private Sale from: ${deal.seller_name}`,
      ].filter(Boolean).join('\n') : '';

      // Rich deal/financing info
      const dealParts = [
        deal?.amount && `Deal Amount: $${Number(deal.amount).toLocaleString()} CAD plus taxes`,
        deal?.downpayment && `Down Payment: $${Number(deal.downpayment).toLocaleString()}`,
        deal?.financing_amount && `Financing Amount: $${Number(deal.financing_amount).toLocaleString()}`,
        deal?.term && `Term: ${deal.term} months`,
        deal?.interest_rate && `Interest Rate: ${deal.interest_rate}%`,
        deal?.monthly_payment && `Monthly Payment: $${Number(deal.monthly_payment).toLocaleString()}`,
        deal?.is_private_sale && 'Transaction Type: Private Sale',
      ].filter(Boolean).join('\n');

      const res = await dealsAPI.generateBrokerNotes({
        client_info: clientParts,
        equipment_info: equipParts,
        deal_info: dealParts,
        staff_notes: staffBrokerInput || brokerNotes || 'Generate professional broker notes for lender submission.',
      });
      const newNotes = res.data?.broker_notes || '';
      setBrokerNotes(newNotes);
      await dealsAPI.update(dealId, { broker_notes: newNotes });
      toast.success('Broker notes generated & saved');
    } catch { toast.error('AI generation failed'); }
    setAiLoading(false);
  };

  const handleSaveBrokerNotes = async () => {
    try {
      await dealsAPI.update(dealId, { broker_notes: brokerNotes });
      toast.success('Broker notes saved');
    } catch { toast.error('Failed to save'); }
  };

  // Equipment Picker
  const openEquipmentPicker = async () => {
    setEquipOpen(true);
    setLoadingInv(true);
    try {
      const res = await inventoryAPI.list({ limit: 200 });
      setInventory(res.data?.items || res.data || []);
    } catch { toast.error('Failed to load inventory'); }
    setLoadingInv(false);
  };

  const handleSelectEquipment = async (unit) => {
    try {
      await dealsAPI.update(dealId, { inventory_id: unit.id, custom_equipment: null });
      toast.success(`Equipment set to ${unit.year} ${unit.make} ${unit.model}`);
      setEquipOpen(false);
      load();
    } catch { toast.error('Failed to update equipment'); }
  };

  const handleSetTBD = async () => {
    try {
      await dealsAPI.update(dealId, { inventory_id: null, custom_equipment: { year: '', make: 'TBD', model: '', notes: 'Equipment to be determined' } });
      toast.success('Equipment set to TBD');
      setEquipOpen(false);
      load();
    } catch { toast.error('Failed to update'); }
  };

  const handleSaveCustomEquip = async () => {
    if (!customEquip.make.trim()) { toast.error('Make is required'); return; }
    try {
      await dealsAPI.update(dealId, { inventory_id: null, custom_equipment: customEquip });
      toast.success('Custom equipment saved');
      setCustomEquipOpen(false);
      setEquipOpen(false);
      load();
    } catch { toast.error('Failed to save'); }
  };

  const filteredInventory = inventory.filter(u => {
    if (!invSearch) return true;
    const s = invSearch.toLowerCase();
    return `${u.year} ${u.make} ${u.model} ${u.stock_number}`.toLowerCase().includes(s);
  });

  // Equipment management handlers for "View All" modal
  const handleRemoveUnit = async (unitId) => {
    const currentIds = deal?.inventory_ids || [];
    const newIds = currentIds.filter(id => id !== unitId);
    setSavingEquip(true);
    try {
      await dealsAPI.update(dealId, {
        inventory_ids: newIds,
        inventory_id: newIds[0] || null,
      });
      toast.success('Unit removed from deal');
      load();
    } catch { toast.error('Failed to remove unit'); }
    setSavingEquip(false);
  };

  const handleAddUnitToDeal = async (unitId) => {
    const currentIds = deal?.inventory_ids || [];
    if (currentIds.includes(unitId)) { toast.info('Unit already in deal'); return; }
    const newIds = [...currentIds, unitId];
    setSavingEquip(true);
    try {
      await dealsAPI.update(dealId, {
        inventory_ids: newIds,
        inventory_id: newIds[0],
      });
      toast.success('Unit added to deal');
      setAddEquipMode(false);
      load();
    } catch { toast.error('Failed to add unit'); }
    setSavingEquip(false);
  };

  const filteredAddInventory = inventory.filter(u => {
    const currentIds = deal?.inventory_ids || [];
    if (currentIds.includes(u.id)) return false;
    if (!addEquipSearch) return true;
    const s = addEquipSearch.toLowerCase();
    return `${u.year} ${u.make} ${u.model} ${u.stock_number}`.toLowerCase().includes(s);
  });

  // Save price override for a unit in the deal
  const handleSaveEditPrice = async (unitId) => {
    setSavingEquip(true);
    try {
      const overrides = { ...(deal?.price_overrides || {}) };
      if (priceOverride && parseFloat(priceOverride) > 0) {
        overrides[unitId] = parseFloat(priceOverride);
      } else {
        delete overrides[unitId];
      }
      await dealsAPI.update(dealId, { price_overrides: overrides });
      toast.success('Deal price updated');
      setEditUnitOpen(false);
      load();
    } catch { toast.error('Failed to update price'); }
    setSavingEquip(false);
  };

  // Swap unit: replace one unit with another in the deal
  const handleSwapUnit = async (oldUnitId, newUnit) => {
    const currentIds = deal?.inventory_ids || [];
    const newIds = currentIds.map(id => id === oldUnitId ? newUnit.id : id);
    setSavingEquip(true);
    try {
      const updatePayload = {
        inventory_ids: newIds,
        inventory_id: newIds[0],
      };
      // If price override is set, store it
      if (priceOverride) {
        updatePayload.price_overrides = { ...(deal?.price_overrides || {}), [newUnit.id]: parseFloat(priceOverride) };
      }
      await dealsAPI.update(dealId, updatePayload);
      toast.success(`Swapped to ${newUnit.year} ${newUnit.make} ${newUnit.model}`);
      setEditUnitOpen(false);
      load();
    } catch { toast.error('Failed to swap unit'); }
    setSavingEquip(false);
  };

  const filteredSwapInventory = inventory.filter(u => {
    const currentIds = deal?.inventory_ids || [];
    if (currentIds.includes(u.id)) return false;
    if (!swapSearch) return true;
    const s = swapSearch.toLowerCase();
    return `${u.year} ${u.make} ${u.model} ${u.stock_number}`.toLowerCase().includes(s);
  });

  const viewDealDoc = async (doc) => {
    try {
      const res = await dealsAPI.downloadDoc(dealId, doc.id);
      const blob = new Blob([res.data], { type: doc.content_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const ext = doc.filename?.split('.').pop()?.toLowerCase() || '';
      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext) || (doc.content_type && doc.content_type.startsWith('image/'));
      const isPdf = ext === 'pdf' || doc.content_type === 'application/pdf';
      setLightbox({ open: true, url, filename: doc.filename, type: isImage ? 'image' : isPdf ? 'pdf' : 'other' });
    } catch { toast.error('Failed to load document'); }
  };

  const downloadDealDoc = async (doc) => {
    try {
      const res = await dealsAPI.downloadDoc(dealId, doc.id);
      const blob = new Blob([res.data], { type: doc.content_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  if (loading) return <div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-gray-100 rounded-lg" />)}</div>;
  if (!deal) return <div className="text-center py-20 text-gray-400">Deal not found</div>;

  const handleSendDocEmail = async () => {
    if (!sendEmailDoc) return;
    setSendingEmail(true);
    try {
      const payload = { send_to: sendEmailMode };
      if (sendEmailMode === 'custom') payload.custom_email = sendEmailCustom;
      if (sendEmailMode === 'lender') payload.lender_id = sendEmailLenderId;
      const res = await documentsAPI.resend(sendEmailDoc.id, payload);
      toast.success(res.data?.message || 'Email sent!');
      setSendEmailOpen(false);
      setSendEmailDoc(null);
      setSendEmailMode('client');
      setSendEmailCustom('');
      setSendEmailLenderId('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    }
    setSendingEmail(false);
  };

  const notes = deal?.deal_notes || [];
  const docs = deal?.documents || [];
  const uploads = deal?.uploads || [];
  const dealDocs = deal?.deal_docs || [];
  const esignReqs = deal?.esign_requests || [];

  return (
    <div data-testid="deal-detail-page" className="max-w-7xl mx-auto">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left Column - 70% - Header + All Deal Info */}
        <div className="w-full lg:w-[65%] space-y-6">
          {/* Header */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/clients/${clientId}`)} className="text-gray-500" data-testid="back-to-portfolio">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Portfolio
            </Button>
          </div>

          {/* Deal Title + Status */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-gray-900" data-testid="deal-title">{deal.title || 'Untitled Deal'}</h1>
                <Badge className={`text-xs px-3 py-1 ${stageColors[deal.stage] || 'bg-gray-200 text-gray-700'}`} data-testid="deal-status-badge">
                  {stageLabels[deal.stage] || deal.stage}
                </Badge>
                <Button variant="outline" size="sm" onClick={openEdit} className="border-gray-300 h-7" data-testid="edit-deal-btn">
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {deal.client?.name && <span>Client: {deal.client.name} &middot; </span>}
                Created {new Date(deal.created_at).toLocaleDateString()}
                {deal.created_by && <span> by {deal.created_by}</span>}
                {deal.funding_date && <span> &middot; Funding: {new Date(deal.funding_date).toLocaleDateString()}</span>}
              </p>
            </div>
          </div>

          {/* Action Buttons Bar */}
          <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <Button size="sm" className="bg-[#E31937] hover:bg-[#C4122C] text-white text-xs w-full sm:w-auto" onClick={() => setHoldDepositOpen(true)} data-testid="action-hold-deposit">
              <DollarSign className="w-3 h-3 mr-1" /> Send Hold Deposit
            </Button>
            <Button size="sm" variant="outline" className="border-gray-300 text-xs w-full sm:w-auto" onClick={handleSendUploadLink} data-testid="action-upload-link">
              <LinkIcon className="w-3 h-3 mr-1" /> Send Secure Upload Link
            </Button>
            <Button size="sm" variant="outline" className="border-gray-300 text-xs w-full sm:w-auto" onClick={() => {
              setDocForm({
                type: 'pre_approval',
                items: [{ description: deal?.unit?.title || '', amount: deal?.amount?.toString() || '' }],
                notes: '', subtotal: '', tax: '', total: '', deposit_amount: '',
                invoice_number: '',
                for_lender: false, lender_id: '', sale_condition: 'as_is',
                ship_to_same: true, ship_to_name: '', ship_to_address: '', ship_to_city: '', ship_to_phone: '',
                custom_price: '', doc_fee: '', licence_fee: '', trade_in_amount: '', payment_method: 'Certified Funds',
                pricing_mode: 'listing', unit_prices: {},
              });
              setDocDialog(true);
            }} data-testid="action-create-doc">
              <FileText className="w-3 h-3 mr-1" /> Create Document
            </Button>
            <Button size="sm" variant="outline" className="border-purple-300 text-purple-700 text-xs w-full sm:w-auto" onClick={openDocSignDialog} data-testid="action-doc-sign">
              <Files className="w-3 h-3 mr-1" /> Send Document for Signing
            </Button>
            {deal?.stage === 'submitted' || deal?.stage === 'approved' || deal?.stage === 'funded' ? (
              <Button size="sm" variant="outline" className="border-purple-300 text-purple-600 text-xs w-full sm:w-auto cursor-default" disabled data-testid="action-submit-credit">
                <CheckCircle className="w-3 h-3 mr-1" /> Submitted to Credit
              </Button>
            ) : (
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white text-xs w-full sm:w-auto" onClick={handleSubmitToCredit} disabled={submittingCredit} data-testid="action-submit-credit">
                <CreditCard className="w-3 h-3 mr-1" /> {submittingCredit ? 'Submitting...' : 'Submit to Credit'}
              </Button>
            )}
            <div className="hidden sm:block h-4 w-px bg-gray-300 mx-1" />
          </div>
          {/* Row 1: Deal Info + Client Info side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            {/* Deal Info Card */}
            <Card className="border-gray-200">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-gray-900 uppercase mb-3">Deal Information</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Amount:</span><span className="font-medium">{deal.amount ? `$${deal.amount.toLocaleString()}` : '—'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Stage:</span><span className="font-medium">{stageLabels[deal.stage] || deal.stage}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Salesperson:</span><span className="font-medium">{deal.client?.salesperson || deal.created_by || '—'}</span></div>
                </div>
                {(deal.notes || deal.client?.notes) && (
                  <div className="mt-3 space-y-2">
                    {deal.notes && (
                      <div className="bg-gray-50 p-2 rounded">
                        <p className="text-[10px] text-gray-400 uppercase mb-0.5">Deal Notes</p>
                        <p className="text-sm text-gray-600">{deal.notes}</p>
                      </div>
                    )}
                    {deal.client?.notes && (
                      <div className="bg-gray-50 p-2 rounded">
                        <p className="text-[10px] text-gray-400 uppercase mb-0.5">Client Notes</p>
                        <p className="text-sm text-gray-600">{deal.client.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Client Info Card */}
            <Card className="border-gray-200">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-gray-900 uppercase mb-3">Client Information</h3>
                {deal.client ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Name:</span><span className="font-medium text-gray-900">{deal.client.name}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Email:</span><span className="font-medium text-gray-900">{deal.client.email}</span></div>
                    {deal.client.phone && <div className="flex justify-between"><span className="text-gray-500">Phone:</span><span className="font-medium text-gray-900">{deal.client.phone}</span></div>}
                    {deal.client.company && <div className="flex justify-between"><span className="text-gray-500">Company:</span><span className="font-medium text-gray-900">{deal.client.company}</span></div>}
                    {deal.client.address && <div className="flex justify-between"><span className="text-gray-500">Address:</span><span className="font-medium text-gray-900">{deal.client.address}</span></div>}
                  </div>
                ) : (
                  <p className="text-gray-400 italic text-sm">No client info</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Row 2: Equipment + Financing side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            {/* Equipment Card */}
            <Card className="border-gray-200">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-[#E31937]" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase">Equipment</h3>
                  </div>
                </div>
                {deal.units && deal.units.length > 0 ? (
                  <div className="space-y-3">
                    {deal.units.slice(0, 1).map((u, idx) => (
                      <div key={u.id || idx} className={`space-y-1.5 text-sm ${idx > 0 ? 'pt-2 border-t border-gray-100' : ''}`}>
                        {deal.units.length > 1 && <p className="text-[10px] text-gray-400 uppercase font-medium">Unit {idx + 1}</p>}
                        <div className="flex justify-between"><span className="text-gray-500">Vehicle</span><span className="font-medium text-gray-900">{u.year} {u.make} {u.model}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Stock #</span><span className="font-medium text-gray-900">{u.stock_number || '—'}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Mileage</span><span className="font-medium text-gray-900">{u.mileage_km ? `${Number(u.mileage_km).toLocaleString()} km` : '—'}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Price</span><span className="font-medium text-[#E31937]">{u.price ? `$${Number(u.price).toLocaleString()}` : '—'}</span></div>
                        <div className="flex gap-1.5 pt-1">
                          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-gray-600 hover:text-[#E31937] hover:border-[#E31937]/30"
                            onClick={() => { setEditUnitData(u); setEditUnitMode('edit'); setPriceOverride(deal?.price_overrides?.[u.id]?.toString() || ''); setEditUnitOpen(true); }}
                            data-testid={`edit-unit-${u.id || idx}`}
                          ><Pencil className="w-2.5 h-2.5 mr-1" />Edit</Button>
                          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-gray-600 hover:text-blue-600 hover:border-blue-300"
                            onClick={() => { setEditUnitData(u); setEditUnitMode('swap'); setSwapSearch(''); setPriceOverride(''); openEquipmentPicker(); setEditUnitOpen(true); }}
                            data-testid={`swap-unit-${u.id || idx}`}
                          ><RefreshCw className="w-2.5 h-2.5 mr-1" />Swap</Button>
                          {(deal.units.length > 1) && (
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-red-400 hover:text-red-600 hover:border-red-300"
                              onClick={() => handleRemoveUnit(u.id)}
                              disabled={savingEquip}
                              data-testid={`delete-unit-${u.id || idx}`}
                            ><Trash2 className="w-2.5 h-2.5 mr-1" />Delete</Button>
                          )}
                        </div>
                      </div>
                    ))}
                    {deal.units.length > 1 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full text-xs border-[#E31937]/30 text-[#E31937] hover:bg-[#E31937]/5 mt-1"
                        onClick={() => { setViewAllEquipOpen(true); setAddEquipMode(false); setAddEquipSearch(''); }}
                        data-testid="view-all-equipment-btn"
                      >
                        <Eye className="w-3 h-3 mr-1" /> View all {deal.units.length} units
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="w-full text-xs border-dashed border-gray-300 text-gray-500 hover:text-[#E31937] hover:border-[#E31937]/30"
                      onClick={openEquipmentPicker}
                      data-testid="add-equipment-btn"
                    ><Plus className="w-3 h-3 mr-1" /> Add Unit</Button>
                  </div>
                ) : deal.unit ? (
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Vehicle</span><span className="font-medium text-gray-900">{deal.unit.year} {deal.unit.make} {deal.unit.model}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Stock #</span><span className="font-medium text-gray-900">{deal.unit.stock_number || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Mileage</span><span className="font-medium text-gray-900">{deal.unit.mileage_km ? `${Number(deal.unit.mileage_km).toLocaleString()} km` : '—'}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Price</span><span className="font-medium text-[#E31937]">{deal.unit.price ? `$${Number(deal.unit.price).toLocaleString()}` : '—'}</span></div>
                    <div className="flex gap-1.5 pt-1">
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-gray-600 hover:text-[#E31937] hover:border-[#E31937]/30"
                        onClick={() => { setEditUnitData(deal.unit); setEditUnitMode('edit'); setPriceOverride(deal?.price_overrides?.[deal.unit.id]?.toString() || ''); setEditUnitOpen(true); }}
                        data-testid="edit-single-unit"
                      ><Pencil className="w-2.5 h-2.5 mr-1" />Edit</Button>
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-gray-600 hover:text-blue-600 hover:border-blue-300"
                        onClick={() => { setEditUnitData(deal.unit); setEditUnitMode('swap'); setSwapSearch(''); setPriceOverride(''); openEquipmentPicker(); setEditUnitOpen(true); }}
                        data-testid="swap-single-unit"
                      ><RefreshCw className="w-2.5 h-2.5 mr-1" />Swap</Button>
                    </div>
                  </div>
                ) : deal.custom_equipment ? (
                  <div className="space-y-1.5 text-sm">
                    {deal.custom_equipment.make === 'TBD' ? (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">Equipment TBD</Badge>
                    ) : (
                      <>
                        <div className="flex justify-between"><span className="text-gray-500">Vehicle</span><span className="font-medium text-gray-900">{deal.custom_equipment.year} {deal.custom_equipment.make} {deal.custom_equipment.model}</span></div>
                        {deal.custom_equipment.price && <div className="flex justify-between"><span className="text-gray-500">Price</span><span className="font-medium text-[#E31937]">${Number(deal.custom_equipment.price).toLocaleString()}</span></div>}
                        <Badge variant="outline" className="text-[9px] border-blue-300 text-blue-600">Private Sale / Custom</Badge>
                        {deal.custom_equipment.notes && <p className="text-xs text-gray-500 mt-1">{deal.custom_equipment.notes}</p>}
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400 italic text-sm">No equipment selected</p>
                )}
              </CardContent>
            </Card>

            {/* Financing Card */}
            <Card className="border-gray-200">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-gray-900 uppercase mb-3">Financing Terms</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Financing Amount', val: deal.financing_amount, fmt: v => `$${v.toLocaleString()}` },
                    { label: 'Monthly Payment', val: deal.monthly_payment, fmt: v => `$${v.toLocaleString()}` },
                    { label: 'Term', val: deal.term, fmt: v => `${v} months` },
                    { label: 'Interest Rate', val: deal.interest_rate, fmt: v => `${v}%` },
                    { label: 'Down Payment', val: deal.downpayment, fmt: v => `$${v.toLocaleString()}` },
                    { label: 'Funding Date', val: deal.funding_date, fmt: v => new Date(v).toLocaleDateString() },
                  ].map(f => (
                    <div key={f.label} className="bg-gray-50 rounded-lg p-2.5">
                      <p className="text-[10px] text-gray-400 uppercase">{f.label}</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">{f.val ? f.fmt(f.val) : '—'}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Documents */}
          <Card className="border-gray-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 uppercase">Documents</h3>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="border-gray-300 text-xs h-7" onClick={() => document.getElementById('portfolio-file-upload').click()} data-testid="portfolio-upload-btn">
                      {uploadingDoc ? <Clock className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                      {uploadingDoc ? 'Uploading...' : 'Upload'}
                    </Button>
                    <input id="portfolio-file-upload" type="file" className="hidden" multiple onChange={handlePortfolioUpload} disabled={uploadingDoc} data-testid="portfolio-upload-input" />
                  <span className="text-xs text-gray-400">{docs.length + uploads.length + dealDocs.length} file(s)</span>
                </div>
              </div>
              {docs.length === 0 && uploads.length === 0 && dealDocs.length === 0 ? (
                <p className="text-sm text-gray-400 italic py-4 text-center">No documents yet</p>
              ) : (
                <div className="space-y-1.5">
                  {docs.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded text-sm" data-testid={`doc-item-${doc.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-[#E31937] flex-shrink-0" />
                        <span className="capitalize font-medium text-gray-800">{doc.type?.replace(/_/g, ' ')}</span>
                        {doc.total && <span className="text-[#E31937] font-bold text-xs">${doc.total.toLocaleString()}</span>}
                        <span className="text-gray-400 text-xs">{new Date(doc.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => window.open(`${API_URL}/api/documents/${doc.id}/download`, '_blank')} data-testid={`view-doc-${doc.id}`}>
                          <Eye className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { const a = document.createElement('a'); a.href = `${API_URL}/api/documents/${doc.id}/download`; a.click(); }} data-testid={`download-doc-${doc.id}`}>
                          <Download className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-500 hover:text-blue-700" onClick={() => { setSendEmailDoc(doc); setSendEmailMode('client'); setSendEmailOpen(true); }} data-testid={`send-doc-${doc.id}`} title="Send via Email">
                          <Send className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => handleDeleteGeneratedDoc(doc.id, doc.type?.replace(/_/g, ' '))} data-testid={`delete-doc-${doc.id}`} title="Delete document">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {dealDocs.map(dd => (
                    <div key={dd.id} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded text-sm" data-testid={`deal-doc-${dd.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Upload className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <span className="text-gray-700 truncate">{dd.filename}</span>
                        {editingCatId === dd.id ? (
                          <Select value={dd.doc_type || ''} onValueChange={v => handleUpdateDocCategory(dd.id, v)}>
                            <SelectTrigger className="h-6 text-[10px] w-auto min-w-[120px] bg-white border-blue-300" data-testid={`edit-cat-select-${dd.id}`}>
                              <SelectValue placeholder="Category..." />
                            </SelectTrigger>
                            <SelectContent>
                              {DOC_CATEGORIES.map(cat => (
                                <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className="text-[9px] border-blue-300 text-blue-600 cursor-pointer hover:bg-blue-100" onClick={() => setEditingCatId(dd.id)} data-testid={`edit-cat-badge-${dd.id}`}>{dd.doc_type} <Pencil className="w-2 h-2 ml-1 inline" /></Badge>
                        )}
                        <span className="text-gray-400 text-[10px]">{new Date(dd.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => viewDealDoc(dd)} data-testid={`view-deal-doc-${dd.id}`}>
                          <Eye className="w-3 h-3 text-blue-600" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => downloadDealDoc(dd)} data-testid={`dl-deal-doc-${dd.id}`}>
                          <Download className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => handleDeleteDealDoc(dd.id)} data-testid={`del-deal-doc-${dd.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {uploads.map(up => (
                    <div key={up.id} className="flex items-center justify-between bg-green-50 px-3 py-2 rounded text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <Upload className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 truncate">{up.original_name}</span>
                        {editingCatId === up.id ? (
                          <Select value={up.doc_type || ''} onValueChange={v => handleUpdateDocCategory(up.id, v)}>
                            <SelectTrigger className="h-6 text-[10px] w-auto min-w-[120px] bg-white border-green-300" data-testid={`edit-cat-select-${up.id}`}>
                              <SelectValue placeholder="Category..." />
                            </SelectTrigger>
                            <SelectContent>
                              {DOC_CATEGORIES.map(cat => (
                                <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className="text-[9px] border-green-400 text-green-600 cursor-pointer hover:bg-green-100" onClick={() => setEditingCatId(up.id)} data-testid={`edit-cat-badge-${up.id}`}>{up.doc_type || 'Uncategorized'} <Pencil className="w-2 h-2 ml-1 inline" /></Badge>
                        )}
                        <Badge variant="outline" className="text-[9px] border-green-300 text-green-500">Client</Badge>
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => window.open(`${API_URL}${up.url}`, '_blank')}>
                        <Download className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* E-Sign Requests */}
          {esignReqs.length > 0 && (
            <Card className="border-gray-200">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-gray-900 uppercase mb-3">E-Sign Requests</h3>
                <div className="space-y-2">
                  {esignReqs.map(req => (
                    <div key={req.id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded text-sm">
                      <div className="flex items-center gap-2">
                        <PenTool className="w-4 h-4 text-purple-500" />
                        <span className="text-gray-700 capitalize">{req.document_type?.replace(/_/g, ' ')}</span>
                        <span className="text-gray-400 text-xs">for {req.signer_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {req.status === 'signed' ? (
                          <Badge className="bg-green-100 text-green-800 border border-green-300 text-[10px]"><CheckCircle className="w-3 h-3 mr-1" />Signed</Badge>
                        ) : (
                          <>
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px]"><Clock className="w-3 h-3 mr-1" />Pending</Badge>
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => {
                              const link = `${window.location.origin}/esign/${req.token}`;
                              navigator.clipboard.writeText(link);
                              toast.success('E-sign link copied!');
                            }}><Copy className="w-3 h-3 mr-1" />Copy Link</Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Document Signing Requests */}
          {docSignRequests.length > 0 && (
            <Card className="border-gray-200">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-gray-900 uppercase mb-3">Document Signing</h3>
                <div className="space-y-2">
                  {docSignRequests.map(req => (
                    <div key={req.id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded text-sm">
                      <div className="flex items-center gap-2">
                        <Files className="w-4 h-4 text-blue-500" />
                        <span className="text-gray-700">{req.template_name}</span>
                        <span className="text-gray-400 text-xs">for {req.signer_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {req.status === 'signed' ? (
                          <>
                            <Badge className="bg-green-100 text-green-800 border border-green-300 text-[10px]"><CheckCircle className="w-3 h-3 mr-1" />Signed</Badge>
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => navigate(`/admin/audit/${req.token}`)} data-testid={`audit-${req.id}`}>
                              <Shield className="w-3 h-3 mr-1" />Audit
                            </Button>
                          </>
                        ) : (
                          <>
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px]"><Clock className="w-3 h-3 mr-1" />Pending</Badge>
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => {
                              const link = `${window.location.origin}/sign/${req.token}`;
                              navigator.clipboard.writeText(link);
                              toast.success('Signing link copied!');
                            }}><Copy className="w-3 h-3 mr-1" />Copy Link</Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - 35% - Full Page Height Sidebar */}
        <div className="w-full lg:w-[35%] lg:min-w-[320px]">
          <div className="lg:sticky lg:top-2 flex flex-col gap-3 lg:h-[calc(100vh-4rem)]">
            {/* Hold Deposit Status */}
            {deal.hold_deposit_status && (
              <Card className={`border-2 flex-shrink-0 ${deal.hold_deposit_status === 'received' ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {deal.hold_deposit_status === 'received' ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Clock className="w-5 h-5 text-amber-600" />}
                    <h4 className="text-sm font-semibold">{deal.hold_deposit_status === 'received' ? 'Deposit Received' : 'Deposit Pending'}</h4>
                  </div>
                  <p className="text-lg font-bold text-gray-900">${deal.hold_deposit_amount?.toLocaleString() || '0'}</p>
                  {deal.hold_deposit_payment_url && deal.hold_deposit_status !== 'received' && (
                    <a href={deal.hold_deposit_payment_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline mt-1 block">Payment Link</a>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Broker Notes - Resizable whole box */}
            <Card className="border-gray-200 flex-1 flex flex-col min-h-[250px] resize-y overflow-hidden" style={{ resize: 'vertical' }}>
              <CardContent className="p-4 flex flex-col h-full">
                <div className="flex items-center justify-between mb-3 flex-shrink-0">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase flex items-center gap-1">
                    <FileText className="w-4 h-4" /> Broker Notes
                  </h3>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="text-xs h-6 border-[#0E7F8E] text-[#0E7F8E] hover:bg-[#0E7F8E]/5 px-2" onClick={() => setLenderPkgPreviewOpen(true)} data-testid="preview-lender-pkg-btn">
                      <Eye className="w-3 h-3 mr-1" /> Preview Pkg
                    </Button>
                    {brokerNotes && (isAdmin || !['approved', 'funded'].includes(deal?.stage)) && (
                      <Button size="sm" variant="outline" className="text-xs h-6 border-gray-300 px-2" onClick={handleSaveBrokerNotes} data-testid="save-broker-notes-btn">
                        Save
                      </Button>
                    )}
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-6 px-2" onClick={handleGenerateBrokerNotes} disabled={aiLoading} data-testid="ai-generate-btn">
                      {aiLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                      {aiLoading ? 'Generating...' : 'AI Generate'}
                    </Button>
                  </div>
                </div>
                <Input
                  placeholder="Optional: context for AI..."
                  value={staffBrokerInput}
                  onChange={e => setStaffBrokerInput(e.target.value)}
                  className="bg-gray-50 border-gray-200 text-xs mb-2 h-7 flex-shrink-0"
                  data-testid="staff-broker-input"
                />
                <Textarea
                  placeholder="Broker notes will appear here..."
                  value={brokerNotes}
                  onChange={e => setBrokerNotes(e.target.value)}
                  readOnly={!isAdmin && ['approved', 'funded'].includes(deal?.stage)}
                  className={`bg-gray-50 border-gray-200 text-sm flex-1 min-h-0 resize-none ${!isAdmin && ['approved', 'funded'].includes(deal?.stage) ? 'opacity-70 cursor-not-allowed' : ''}`}
                  data-testid="broker-notes-textarea"
                />
                {!isAdmin && ['approved', 'funded'].includes(deal?.stage) && (
                  <p className="text-[10px] text-gray-400 mt-1 flex-shrink-0">Broker notes are locked after deal approval</p>
                )}
                {!brokerNotes.trim() && (
                  <p className="text-[10px] text-amber-600 mt-1 flex-shrink-0">Broker notes required before submitting to credit</p>
                )}
              </CardContent>
            </Card>

            {/* Activity Log - Collapsible at bottom */}
            <Card className="border-gray-200 flex-shrink-0">
              <CardContent className="p-4">
                <button
                  type="button"
                  className="flex items-center justify-between w-full"
                  onClick={() => setActivityOpen(prev => !prev)}
                  data-testid="activity-log-toggle"
                >
                  <h3 className="text-sm font-semibold text-gray-900 uppercase flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-gray-500" /> Activity Log
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-400">{activityLog.length} events</span>
                    {activityOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </button>
                {activityOpen && (
                  <div className="mt-3">
                    {activityLog.length === 0 ? (
                      <p className="text-xs text-gray-400 italic text-center py-4">No activity recorded yet</p>
                    ) : (
                      <div className="max-h-[280px] overflow-y-auto space-y-0 pr-1" data-testid="activity-log-list">
                        {activityLog.map((act, i) => {
                          const icon = {
                            link_sent: <LinkIcon className="w-3 h-3 text-blue-500" />,
                            link_opened: <Eye className="w-3 h-3 text-amber-500" />,
                            client_upload: <Upload className="w-3 h-3 text-green-500" />,
                            staff_upload: <Upload className="w-3 h-3 text-blue-600" />,
                            stage_change: <RefreshCw className="w-3 h-3 text-purple-500" />,
                            note_added: <MessageSquare className="w-3 h-3 text-gray-500" />,
                            credit_submitted: <CreditCard className="w-3 h-3 text-purple-600" />,
                            credit_package_sent: <Send className="w-3 h-3 text-red-500" />,
                            deposit_sent: <DollarSign className="w-3 h-3 text-teal-500" />,
                            esign_sent: <PenTool className="w-3 h-3 text-purple-500" />,
                            esign_signed: <CheckCircle className="w-3 h-3 text-green-600" />,
                            doc_sign_sent: <Files className="w-3 h-3 text-purple-500" />,
                            doc_viewed: <Eye className="w-3 h-3 text-amber-500" />,
                            doc_signed: <CheckCircle className="w-3 h-3 text-green-600" />,
                            equipment_change: <Truck className="w-3 h-3 text-orange-500" />,
                            broker_notes_saved: <FileText className="w-3 h-3 text-indigo-500" />,
                          }[act.type] || <Clock className="w-3 h-3 text-gray-400" />;
                          const timeStr = act.created_at ? new Date(act.created_at).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                          return (
                            <div key={act.id || i} className="flex gap-2.5 py-2 border-b border-gray-50 last:border-0" data-testid={`activity-item-${i}`}>
                              <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center">
                                {icon}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-[11px] text-gray-700 leading-snug">{act.message}</p>
                                <p className="text-[10px] text-gray-400 mt-0.5">{timeStr}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Deal Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-deal-dialog">
          <DialogHeader><DialogTitle>Edit Deal</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-3">
            <div><label className="text-xs text-gray-500">Title</label><Input value={editForm.title || ''} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" data-testid="edit-deal-title" /></div>
            <div>
              <label className="text-xs text-gray-500">Stage</label>
              <Select value={editForm.stage || 'lead'} onValueChange={v => setEditForm(f => ({ ...f, stage: v }))}>
                <SelectTrigger className="bg-gray-50 border-gray-200 mt-1" data-testid="edit-deal-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(stageLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-gray-500">Deal Amount ($)</label><Input type="number" value={editForm.amount || ''} onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" /></div>
            <div><label className="text-xs text-gray-500">Notes</label><Textarea value={editForm.notes || ''} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" rows={2} /></div>
            <Separator />
            <p className="text-xs text-gray-500 font-semibold uppercase">Financing Fields</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="text-xs text-gray-500">Financing Amount</label><Input type="number" value={editForm.financing_amount || ''} onChange={e => setEditForm(f => ({ ...f, financing_amount: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" /></div>
              <div><label className="text-xs text-gray-500">Monthly Payment</label><Input type="number" value={editForm.monthly_payment || ''} onChange={e => setEditForm(f => ({ ...f, monthly_payment: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" /></div>
              <div><label className="text-xs text-gray-500">Term (months)</label><Input type="number" value={editForm.term || ''} onChange={e => setEditForm(f => ({ ...f, term: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" /></div>
              <div><label className="text-xs text-gray-500">Interest Rate (%)</label><Input type="number" step="0.01" value={editForm.interest_rate || ''} onChange={e => setEditForm(f => ({ ...f, interest_rate: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" /></div>
              <div><label className="text-xs text-gray-500">Down Payment</label><Input type="number" value={editForm.downpayment || ''} onChange={e => setEditForm(f => ({ ...f, downpayment: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" /></div>
              <div><label className="text-xs text-gray-500">Funding Date</label><Input type="date" value={editForm.funding_date || ''} onChange={e => setEditForm(f => ({ ...f, funding_date: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" /></div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" className="border-gray-300" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#E31937] text-white hover:bg-[#C4122C]" disabled={saving} data-testid="save-deal-btn">{saving ? 'Saving...' : 'Save Deal'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Document Dialog */}
      <Dialog open={docDialog} onOpenChange={setDocDialog}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto bg-white border-gray-200 text-gray-900" data-testid="create-doc-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#E31937]" /> Create Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500">Type</label>
              <Select value={docForm.type} onValueChange={v => setDocForm(f => ({ ...f, type: v }))}>
                <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pre_approval">Pre-Approval Doc Request</SelectItem>
                  <SelectItem value="invoice">Invoice</SelectItem>
                  <SelectItem value="bill_of_sale">Bill of Sale</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {docForm.type === 'pre_approval' && (
              <div className="space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-medium text-blue-800">This will email the client requesting:</p>
                  <ol className="text-xs text-blue-700 list-decimal pl-5 space-y-0.5">
                    <li>Driver's License</li>
                    <li>Articles of Incorporation</li>
                    <li>Last Three Months' Bank Statements</li>
                    <li>Latest Financials or T2 (if available)</li>
                    <li>Self-Pulled Equifax Credit Report (if available)</li>
                  </ol>
                  <p className="text-xs text-blue-600">A secure upload link will be included. Uploaded docs will appear in this deal.</p>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" className="border-gray-300" onClick={() => setDocDialog(false)}>Cancel</Button>
                  <Button className="bg-[#E31937] text-white" onClick={handleSendPreApproval} disabled={saving} data-testid="send-pre-approval-btn">
                    <Send className="w-3 h-3 mr-1" /> {saving ? 'Sending...' : 'Send to Client'}
                  </Button>
                </div>
              </div>
            )}
            {(docForm.type === 'quote' || docForm.type === 'invoice') && (
              <div>
                <div className="flex justify-between mb-1"><label className="text-xs text-gray-500">Line Items</label><button className="text-xs text-[#E31937]" onClick={addDocItem}>+ Add</button></div>
                {docForm.items.map((it, idx) => (
                  <div key={`item-${idx}-${it.description}`} className="flex gap-2 mb-1.5">
                    <Input placeholder="Description" value={it.description} onChange={e => updateDocItem(idx, 'description', e.target.value)} className="bg-gray-50 border-gray-200 flex-1 text-xs" />
                    <Input placeholder="$" type="number" value={it.amount} onChange={e => updateDocItem(idx, 'amount', e.target.value)} className="bg-gray-50 border-gray-200 w-28 text-xs" />
                  </div>
                ))}
                <div className="text-right text-xs text-gray-500 mt-2">Sub: ${docForm.subtotal || '0'} | HST: ${docForm.tax || '0'} | <span className="text-[#E31937] font-bold">Total: ${docForm.total || '0'}</span></div>
              </div>
            )}
            {docForm.type === 'bill_of_sale' && (
              <div className="space-y-3">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-red-800 mb-1">Professional CTS Bill of Sale</p>
                  <p className="text-xs text-red-600">This will generate a branded, multi-page BOS document with all details below.</p>
                </div>

                {/* Invoice Number — sticky per deal, derived from VIN by default */}
                {(() => {
                  const unitIds = (deal?.inventory_ids?.length ? deal.inventory_ids : [deal?.inventory_id]).filter(Boolean);
                  const firstUnit = (deal?.units || []).find(u => u.id === unitIds[0]) || null;
                  const firstVin = (firstUnit?.vin || '').trim();
                  const vinDerived = firstVin.length >= 4 ? `CTS-${firstVin.slice(-4).toUpperCase()}` : '';
                  const sticky = (deal?.invoice_number || '').trim();
                  const computedDefault = sticky || vinDerived;
                  const placeholder = computedDefault || 'CTS-XXXX';
                  const hint = sticky
                    ? `Saved on this deal. All BOS copies will reuse ${sticky}.`
                    : vinDerived
                      ? `Auto-filled from VIN. Override only if you need a custom number.`
                      : 'No VIN found on equipment — please enter an invoice number (e.g. CTS-1234).';
                  return (
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-gray-600 uppercase">
                        Invoice Number {!vinDerived && !sticky && <span className="text-[#E31937]">*</span>}
                      </label>
                      <Input
                        value={docForm.invoice_number || ''}
                        onChange={e => setDocForm(f => ({ ...f, invoice_number: e.target.value }))}
                        placeholder={placeholder}
                        className="bg-gray-50 border-gray-200 text-sm font-mono uppercase"
                        data-testid="bos-invoice-number"
                      />
                      <p className="text-[11px] text-gray-500" data-testid="bos-invoice-hint">{hint}</p>
                    </div>
                  );
                })()}

                {/* Bill For: Client or Lender */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-600 uppercase">Bill For</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="radio" name="billFor" checked={!docForm.for_lender} onChange={() => setDocForm(f => ({ ...f, for_lender: false, lender_id: '' }))} className="accent-[#E31937]" data-testid="bill-for-client" />
                      For Client
                    </label>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="radio" name="billFor" checked={docForm.for_lender === true} onChange={() => setDocForm(f => ({ ...f, for_lender: true, ship_to_same: true }))} className="accent-[#E31937]" data-testid="bill-for-lender" />
                      For Lender
                    </label>
                  </div>
                  {docForm.for_lender && (
                    <div>
                      <label className="text-xs text-gray-500">Select Lender</label>
                      <Select value={docForm.lender_id || ''} onValueChange={v => setDocForm(f => ({ ...f, lender_id: v }))}>
                        <SelectTrigger className="bg-gray-50 border-gray-200 mt-1 text-xs" data-testid="lender-select"><SelectValue placeholder="Choose a lender..." /></SelectTrigger>
                        <SelectContent>
                          {lenders.map(l => <SelectItem key={l.id} value={l.id}>{l.name}{l.address ? ` — ${l.address}` : ''}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-gray-400 mt-0.5">Buyer = Lender info, Ship To = Client info</p>
                    </div>
                  )}
                </div>

                {/* Condition of Sale */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-600 uppercase">Condition of Sale</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="radio" name="saleCondition" checked={docForm.sale_condition !== 'safety_certified'} onChange={() => setDocForm(f => ({ ...f, sale_condition: 'as_is' }))} className="accent-[#E31937]" data-testid="condition-as-is" />
                      Sold As-Is
                    </label>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="radio" name="saleCondition" checked={docForm.sale_condition === 'safety_certified'} onChange={() => setDocForm(f => ({ ...f, sale_condition: 'safety_certified' }))} className="accent-[#E31937]" data-testid="condition-safety" />
                      Safety Certified
                    </label>
                  </div>
                </div>

                {/* Ship To (only show when NOT for lender) */}
                {!docForm.for_lender && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-600 uppercase">Ship To</label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="radio" name="shipTo" checked={docForm.ship_to_same !== false} onChange={() => setDocForm(f => ({ ...f, ship_to_same: true }))} className="accent-[#E31937]" />
                      Same as Buyer
                    </label>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="radio" name="shipTo" checked={docForm.ship_to_same === false} onChange={() => setDocForm(f => ({ ...f, ship_to_same: false }))} className="accent-[#E31937]" />
                      Custom Address
                    </label>
                  </div>
                  {docForm.ship_to_same === false && (
                    <div className="grid grid-cols-2 gap-2">
                      <div><Input placeholder="Company / Name" value={docForm.ship_to_name || ''} onChange={e => setDocForm(f => ({ ...f, ship_to_name: e.target.value }))} className="bg-gray-50 border-gray-200 text-xs" /></div>
                      <div><Input placeholder="Phone" value={docForm.ship_to_phone || ''} onChange={e => setDocForm(f => ({ ...f, ship_to_phone: e.target.value }))} className="bg-gray-50 border-gray-200 text-xs" /></div>
                      <div><Input placeholder="Address" value={docForm.ship_to_address || ''} onChange={e => setDocForm(f => ({ ...f, ship_to_address: e.target.value }))} className="bg-gray-50 border-gray-200 text-xs" /></div>
                      <div><Input placeholder="City, Province, Postal Code" value={docForm.ship_to_city || ''} onChange={e => setDocForm(f => ({ ...f, ship_to_city: e.target.value }))} className="bg-gray-50 border-gray-200 text-xs" /></div>
                    </div>
                  )}
                </div>
                )}

                {/* Financial Fields */}
                {/* Pricing Mode (only show for multi-unit deals) */}
                {deal?.inventory_ids?.length > 1 ? (
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-600 uppercase">Pricing</label>
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input type="radio" name="pricingMode" checked={docForm.pricing_mode === 'listing'} onChange={() => setDocForm(f => ({ ...f, pricing_mode: 'listing', custom_price: '', unit_prices: {} }))} className="accent-[#E31937]" data-testid="pricing-listing" />
                        Listing Price
                      </label>
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input type="radio" name="pricingMode" checked={docForm.pricing_mode === 'per_unit'} onChange={() => setDocForm(f => ({ ...f, pricing_mode: 'per_unit', custom_price: '' }))} className="accent-[#E31937]" data-testid="pricing-per-unit" />
                        Custom Per Unit
                      </label>
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input type="radio" name="pricingMode" checked={docForm.pricing_mode === 'single_price'} onChange={() => setDocForm(f => ({ ...f, pricing_mode: 'single_price', unit_prices: {} }))} className="accent-[#E31937]" data-testid="pricing-single" />
                        Single Deal Price
                      </label>
                    </div>
                    {docForm.pricing_mode === 'per_unit' && deal.units?.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-2 space-y-2 border border-gray-100">
                        <p className="text-[10px] text-gray-400 uppercase font-medium">Set price for each unit</p>
                        {deal.units.map((u) => (
                          <div key={u.id} className="flex items-center gap-2">
                            <span className="text-xs text-gray-600 flex-1 truncate">{u.year} {u.make} {u.model}</span>
                            <span className="text-[10px] text-gray-400">List: ${Number(u.price || 0).toLocaleString()}</span>
                            <Input
                              type="number" placeholder="Custom $"
                              value={(docForm.unit_prices || {})[u.id] || ''}
                              onChange={e => setDocForm(f => ({ ...f, unit_prices: { ...(f.unit_prices || {}), [u.id]: e.target.value } }))}
                              className="bg-white border-gray-200 w-32 text-xs"
                              data-testid={`unit-price-${u.id}`}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    {docForm.pricing_mode === 'single_price' && (
                      <div>
                        <label className="text-xs text-gray-500">Total Deal Price ($)</label>
                        <Input type="number" value={docForm.custom_price || ''} onChange={e => setDocForm(f => ({ ...f, custom_price: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" placeholder="Enter total price for all units" data-testid="bos-single-price" />
                        <p className="text-[10px] text-gray-400 mt-0.5">One price for the entire deal — individual listing prices hidden</p>
                      </div>
                    )}
                    {docForm.pricing_mode === 'listing' && (
                      <p className="text-[10px] text-gray-400">Each unit's inventory listing price will be used</p>
                    )}
                  </div>
                ) : (
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 font-semibold">Equipment / Sale Price ($)</label>
                    <Input type="number" value={docForm.custom_price || ''} onChange={e => setDocForm(f => ({ ...f, custom_price: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" placeholder="Leave blank to use listing price" data-testid="bos-custom-price" />
                    <p className="text-[10px] text-gray-400 mt-0.5">Custom price overrides the listing price on the BOS</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Document / Admin Fee ($)</label>
                    <Input type="number" value={docForm.doc_fee || ''} onChange={e => setDocForm(f => ({ ...f, doc_fee: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Licensing Fee ($)</label>
                    <Input type="number" value={docForm.licence_fee || ''} onChange={e => setDocForm(f => ({ ...f, licence_fee: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Trade-In Allowance ($)</label>
                    <Input type="number" value={docForm.trade_in_amount || ''} onChange={e => setDocForm(f => ({ ...f, trade_in_amount: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Hold Deposit ($)</label>
                    <Input type="number" value={docForm.deposit_amount || ''} onChange={e => setDocForm(f => ({ ...f, deposit_amount: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" placeholder="0.00" />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500">Payment Method</label>
                  <Select value={docForm.payment_method || 'Certified Funds'} onValueChange={v => setDocForm(f => ({ ...f, payment_method: v }))}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 mt-1 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Certified Funds">Certified Funds</SelectItem>
                      <SelectItem value="Certified Cheque">Certified Cheque</SelectItem>
                      <SelectItem value="Wire Transfer">Wire Transfer</SelectItem>
                      <SelectItem value="Bank Draft">Bank Draft</SelectItem>
                      <SelectItem value="Financing">Financing</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="sm:col-span-2">
                  <label className="text-xs text-gray-500">Salesperson</label>
                  <Select value={docForm.salesperson_name || ''} onValueChange={v => setDocForm(f => ({ ...f, salesperson_name: v === '__none__' ? '' : v }))}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 mt-1 text-xs" data-testid="bos-salesperson-select">
                      <SelectValue placeholder="Select staff (defaults to current user)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Use current user (default)</SelectItem>
                      {staffList.map(s => (
                        <SelectItem key={s.id} value={s.name}>{s.name}{s.role ? ` — ${s.role}` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {docForm.type === 'hold_deposit' && (
              <div><label className="text-xs text-gray-500">Deposit Amount</label><Input type="number" value={docForm.deposit_amount} onChange={e => setDocForm(f => ({ ...f, deposit_amount: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" /></div>
            )}
            {docForm.type !== 'pre_approval' && (
              <>
                <Textarea placeholder="Notes" value={docForm.notes} onChange={e => setDocForm(f => ({ ...f, notes: e.target.value }))} className="bg-gray-50 border-gray-200" rows={2} />
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" className="border-gray-300" onClick={() => setDocDialog(false)}>Cancel</Button>
                  <Button className="bg-[#E31937] text-white" onClick={handleGenerateDoc} disabled={saving}>{saving ? 'Generating...' : 'Generate & Download PDF'}</Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Hold Deposit Dialog */}
      <Dialog open={holdDepositOpen} onOpenChange={setHoldDepositOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md" data-testid="hold-deposit-dialog">
          <DialogHeader><DialogTitle>Send Hold Deposit Request</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
              <p className="font-semibold text-sm">{deal?.unit?.title || deal?.title || 'Deal'}</p>
              <p className="text-xs text-gray-500">Client: {deal?.client?.name} ({deal?.client?.email})</p>
              {deal?.amount && <p className="text-xs text-gray-500">Deal Amount: ${deal.amount.toLocaleString()}</p>}
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase block mb-1">Hold Deposit Amount ($)</label>
              <Input type="number" value={holdAmount} onChange={e => setHoldAmount(e.target.value)} placeholder="Enter amount..." className="bg-gray-50 border-gray-200" data-testid="hold-deposit-amount" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase block mb-1">Notes (optional)</label>
              <Textarea value={holdNotes} onChange={e => setHoldNotes(e.target.value)} placeholder="Add a note for the client..." className="bg-gray-50 border-gray-200" rows={2} data-testid="hold-deposit-notes" />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
              <p className="font-semibold mb-1">Client will receive:</p>
              <p>1. Online payment link (Square) for ${holdAmount || '0'}</p>
              <p>2. E-transfer details: <span className="font-mono">info@canadiantrucksales.ca</span></p>
            </div>
            <Button className="w-full bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={handleSendHoldDeposit} disabled={sendingDeposit} data-testid="confirm-hold-deposit">
              {sendingDeposit ? 'Sending...' : `Send Hold Deposit ($${holdAmount || '0'})`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* E-Sign Credit App Dialog */}
      <Dialog open={esignOpen} onOpenChange={setEsignOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="esign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5 text-purple-500" />
              {esignStep === 'edit' ? 'Send Credit Application' : 'Review & Send'}
            </DialogTitle>
          </DialogHeader>
          {esignTemplate && esignStep === 'edit' && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 text-sm">
                <p><span className="text-gray-500">Client:</span> <span className="font-medium">{deal?.client?.name}</span></p>
                <p><span className="text-gray-500">Deal:</span> {deal?.title}</p>
              </div>
              <p className="text-xs text-gray-500">Edit prefilled fields before sending. Client can also edit on the signing page.</p>
              {['personal', 'corporate', 'net_worth'].map(section => {
                const sectionFields = (esignTemplate.fields || []).filter(f => f.section === section);
                if (sectionFields.length === 0) return null;
                const sLabel = section === 'personal' ? "Applicant's Personal Information" : section === 'corporate' ? 'Corporate Information' : 'Personal Net Worth';
                return (
                  <div key={section}>
                    <p className="text-[10px] text-white uppercase font-semibold bg-[#E31937] px-2 py-1 rounded mb-2">{sLabel}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {sectionFields.map(f => (
                        <div key={f.id} className={f.type === 'textarea' ? 'col-span-2' : ''}>
                          <label className="text-[10px] text-gray-600">{f.label} {f.required && <span className="text-red-500">*</span>}</label>
                          {f.type === 'select' ? (
                            <Select value={esignFields[f.id] || ''} onValueChange={v => setEsignFields(p => ({ ...p, [f.id]: v }))}>
                              <SelectTrigger className="bg-gray-50 border-gray-200 mt-0.5 h-8 text-xs"><SelectValue placeholder={`Select...`} /></SelectTrigger>
                              <SelectContent>{(f.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : f.type === 'textarea' ? (
                            <Textarea value={esignFields[f.id] || ''} onChange={e => setEsignFields(p => ({ ...p, [f.id]: e.target.value }))} className="bg-gray-50 border-gray-200 mt-0.5 text-xs" rows={2} placeholder={f.label} />
                          ) : (
                            <Input type={f.type} value={esignFields[f.id] || ''} onChange={e => setEsignFields(p => ({ ...p, [f.id]: e.target.value }))} className="bg-gray-50 border-gray-200 mt-0.5 h-8 text-xs" placeholder={f.label} data-testid={`esign-field-${f.id}`} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" className="border-gray-300" onClick={() => setEsignOpen(false)}>Cancel</Button>
                <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={() => setEsignStep('preview')} data-testid="esign-preview-btn">
                  <Eye className="w-3 h-3 mr-1" /> Preview Before Sending
                </Button>
              </div>
            </div>
          )}
          {esignTemplate && esignStep === 'preview' && (
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden max-w-lg mx-auto">
                <div className="bg-[#E31937] text-white text-center py-3">
                  <h3 className="text-base font-bold">CANADIAN TRUCK SALES</h3>
                  <p className="text-[10px] opacity-80">{esignTemplate.title || 'Credit Application'}</p>
                </div>
                <div className="p-4">
                  {['personal', 'corporate', 'net_worth'].map(section => {
                    const sectionFields = (esignTemplate.fields || []).filter(f => f.section === section);
                    if (sectionFields.length === 0) return null;
                    const sLabel = section === 'personal' ? "Personal Information" : section === 'corporate' ? 'Corporate Information' : 'Personal Net Worth';
                    const filledFields = sectionFields.filter(f => esignFields[f.id]);
                    if (filledFields.length === 0) return null;
                    return (
                      <div key={section} className="mb-3">
                        <p className="text-[10px] text-gray-400 uppercase font-bold border-b border-gray-100 pb-1 mb-1.5">{sLabel}</p>
                        <div className="space-y-0.5">
                          {filledFields.map(f => (
                            <div key={f.id} className="flex justify-between text-xs py-0.5">
                              <span className="text-gray-500">{f.label}:</span>
                              <span className="font-medium text-gray-900 text-right max-w-[55%] truncate">{esignFields[f.id]}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                <div className="mt-2 p-3 border border-dashed border-gray-200 rounded text-center text-xs text-gray-400">
                  Client will sign with typed name + IP/timestamp
                </div>
                </div>
              </div>
              <div className="flex gap-2 justify-between pt-2">
                <Button variant="outline" className="border-gray-300" onClick={() => setEsignStep('edit')} data-testid="esign-back-btn">
                  <Pencil className="w-3 h-3 mr-1" /> Edit Fields
                </Button>
                <Button className="bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={handleSendEsign} disabled={sendingEsign} data-testid="confirm-esign">
                  <Send className="w-3 h-3 mr-1" /> {sendingEsign ? 'Creating...' : 'Send Credit Application'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Document Signing Dialog */}
      <Dialog open={docSignOpen} onOpenChange={setDocSignOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md" data-testid="doc-sign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Files className="w-5 h-5 text-purple-500" /> Send Document for Signing</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 text-sm">
              <p><span className="text-gray-500">Client:</span> <span className="font-medium">{deal?.client?.name}</span> ({deal?.client?.email})</p>
              <p><span className="text-gray-500">Deal:</span> {deal?.title}</p>
            </div>
            {docTemplates.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-sm text-gray-400 mb-2">No document templates with uploaded PDFs</p>
                <Button variant="outline" size="sm" onClick={() => { setDocSignOpen(false); navigate('/admin/doc-templates'); }}>Create Template</Button>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-xs text-gray-500 uppercase block mb-1">Select Template</label>
                  <Select value={selectedDocTemplate} onValueChange={setSelectedDocTemplate}>
                    <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="select-doc-template"><SelectValue placeholder="Choose a document template..." /></SelectTrigger>
                    <SelectContent>
                      {docTemplates.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name} ({t.page_count} pages, {(t.fields || []).length} fields)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                  <p className="font-semibold mb-1">How it works:</p>
                  <p>1. A unique signing link is generated for the client</p>
                  <p>2. Client fills in the positioned fields on the document</p>
                  <p>3. Signature, IP, and timestamp are recorded</p>
                  <p>4. Signed document + audit report stored in the deal</p>
                </div>
                <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" onClick={handleSendDocSign} disabled={sendingDocSign || !selectedDocTemplate} data-testid="confirm-doc-sign">
                  <Send className="w-3 h-3 mr-1" /> {sendingDocSign ? 'Creating...' : 'Create Signing Link'}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Equipment Picker Dialog */}
      <Dialog open={equipOpen} onOpenChange={setEquipOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg max-h-[80vh]" data-testid="equipment-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-[#E31937]" /> Select Equipment
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-2 mb-3">
            <Button size="sm" variant="outline" className="text-xs border-blue-300 text-blue-700 flex-1" onClick={() => { setCustomEquip({ year: '', make: '', model: '', price: '', notes: '' }); setCustomEquipOpen(true); }} data-testid="equip-custom-btn">
              Custom / Private Sale
            </Button>
          </div>
          <Input placeholder="Search inventory..." value={invSearch} onChange={e => setInvSearch(e.target.value)} className="bg-gray-50 border-gray-200" data-testid="equipment-search-input" />
          <div className="max-h-[300px] overflow-y-auto space-y-1.5 mt-2">
            {loadingInv ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : filteredInventory.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No available units found</p>
            ) : (
              filteredInventory.map(unit => (
                <div key={unit.id} className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 px-3 py-2.5 rounded cursor-pointer transition-colors" onClick={() => handleAddUnitToDeal(unit.id)} data-testid={`inv-item-${unit.id}`}>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{unit.year} {unit.make} {unit.model}</p>
                    <p className="text-xs text-gray-500">Stock #{unit.stock_number} &middot; {unit.mileage_km ? `${Number(unit.mileage_km).toLocaleString()} km` : '—'}</p>
                  </div>
                  <span className="text-sm font-semibold text-[#E31937]">{unit.price ? `$${Number(unit.price).toLocaleString()}` : '—'}</span>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Equipment Dialog */}
      <Dialog open={customEquipOpen} onOpenChange={setCustomEquipOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md" data-testid="custom-equip-dialog">
          <DialogHeader>
            <DialogTitle>Custom Equipment / Private Sale</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500">Year</label>
                <Input value={customEquip.year} onChange={e => setCustomEquip(f => ({ ...f, year: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" placeholder="2024" data-testid="custom-year" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Make *</label>
                <Input value={customEquip.make} onChange={e => setCustomEquip(f => ({ ...f, make: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" placeholder="Volvo" data-testid="custom-make" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Model</label>
                <Input value={customEquip.model} onChange={e => setCustomEquip(f => ({ ...f, model: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" placeholder="VNL 760" data-testid="custom-model" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Price ($)</label>
                <Input type="number" value={customEquip.price} onChange={e => setCustomEquip(f => ({ ...f, price: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" placeholder="85000" data-testid="custom-price" />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500">Notes / Details</label>
              <Textarea value={customEquip.notes} onChange={e => setCustomEquip(f => ({ ...f, notes: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1 text-sm" placeholder="Private sale details, condition, etc." data-testid="custom-notes" />
            </div>
            <Button className="w-full bg-gray-900 hover:bg-gray-800 text-white" onClick={handleSaveCustomEquip} data-testid="save-custom-equip-btn">
              Save Equipment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Unit Modal */}
      <Dialog open={editUnitOpen} onOpenChange={setEditUnitOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg max-h-[80vh] overflow-y-auto" data-testid="edit-unit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editUnitMode === 'swap' ? <><RefreshCw className="w-5 h-5 text-blue-600" /> Swap Equipment</> : <><Pencil className="w-5 h-5 text-[#E31937]" /> Unit Details</>}
            </DialogTitle>
          </DialogHeader>

          {editUnitData && editUnitMode === 'edit' && (
            <div className="space-y-3">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Vehicle</span><span className="font-semibold text-gray-900">{editUnitData.year} {editUnitData.make} {editUnitData.model}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Stock #</span><span className="font-medium">{editUnitData.stock_number || '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">VIN</span><span className="font-mono text-xs">{editUnitData.vin || '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Mileage</span><span className="font-medium">{editUnitData.mileage_km ? `${Number(editUnitData.mileage_km).toLocaleString()} km` : '—'}</span></div>
                {editUnitData.engine && <div className="flex justify-between"><span className="text-gray-500">Engine</span><span>{editUnitData.engine}</span></div>}
                {editUnitData.horsepower && <div className="flex justify-between"><span className="text-gray-500">HP</span><span>{editUnitData.horsepower}</span></div>}
                {editUnitData.transmission && <div className="flex justify-between"><span className="text-gray-500">Transmission</span><span>{editUnitData.transmission}</span></div>}
                <div className="flex justify-between"><span className="text-gray-500">Listing Price</span><span className="font-semibold text-gray-400">{editUnitData.price ? `$${Number(editUnitData.price).toLocaleString()}` : '—'}</span></div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Deal Price ($)</label>
                <p className="text-[10px] text-gray-400 mb-1">Override the listing price for this deal only</p>
                <div className="flex gap-2">
                  <Input type="number" value={priceOverride} onChange={e => setPriceOverride(e.target.value)}
                    placeholder={editUnitData.price ? `${Number(editUnitData.price).toLocaleString()} (listing)` : 'Enter deal price'}
                    className="bg-gray-50 border-gray-200 text-sm flex-1" data-testid="edit-price-override" />
                  <Button size="sm" className="bg-[#E31937] hover:bg-[#C4122C] text-white text-xs px-4"
                    onClick={() => handleSaveEditPrice(editUnitData.id)} disabled={savingEquip}
                    data-testid="save-edit-price-btn"
                  >{savingEquip ? 'Saving...' : 'Save Price'}</Button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1 text-xs border-blue-300 text-blue-600 hover:bg-blue-50"
                  onClick={() => { setEditUnitMode('swap'); setSwapSearch(''); setPriceOverride(''); openEquipmentPicker(); }}
                  data-testid="switch-to-swap"
                ><RefreshCw className="w-3 h-3 mr-1" /> Swap with another unit</Button>
                {deal?.units?.length > 1 && (
                  <Button size="sm" variant="outline" className="text-xs border-red-300 text-red-500 hover:bg-red-50"
                    onClick={() => { handleRemoveUnit(editUnitData.id); setEditUnitOpen(false); }}
                    disabled={savingEquip}
                    data-testid="delete-from-edit"
                  ><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
                )}
              </div>
            </div>
          )}

          {editUnitData && editUnitMode === 'swap' && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-700">
                Replacing: <span className="font-semibold">{editUnitData.year} {editUnitData.make} {editUnitData.model}</span>
              </div>
              {/* Price Override */}
              <div>
                <label className="text-xs text-gray-500">Price Override (optional)</label>
                <Input type="number" value={priceOverride} onChange={e => setPriceOverride(e.target.value)} placeholder="Leave empty to use listing price" className="bg-gray-50 border-gray-200 mt-1 text-sm" data-testid="swap-price-override" />
              </div>
              {/* Search */}
              <Input placeholder="Search available inventory..." value={swapSearch} onChange={e => setSwapSearch(e.target.value)} className="bg-gray-50 border-gray-200" data-testid="swap-search" />
              <div className="max-h-[250px] overflow-y-auto space-y-1.5">
                {loadingInv ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                ) : filteredSwapInventory.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No available units</p>
                ) : filteredSwapInventory.map(unit => (
                  <div key={unit.id} className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 px-3 py-2.5 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-blue-200"
                    onClick={() => handleSwapUnit(editUnitData.id, unit)}
                    data-testid={`swap-pick-${unit.id}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{unit.year} {unit.make} {unit.model}</p>
                      <p className="text-xs text-gray-500">Stock #{unit.stock_number}</p>
                    </div>
                    <span className="text-sm font-semibold text-[#E31937]">{unit.price ? `$${Number(unit.price).toLocaleString()}` : '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View All Equipment Modal */}
      <Dialog open={viewAllEquipOpen} onOpenChange={setViewAllEquipOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="view-all-equipment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-[#E31937]" /> All Equipment ({deal?.units?.length || 0} units)
            </DialogTitle>
          </DialogHeader>

          {!addEquipMode ? (
            <div className="space-y-3">
              {/* Unit list */}
              <div className="space-y-2" data-testid="equipment-list">
                {(deal?.units || []).map((u, idx) => (
                  <div key={u.id || idx} className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid={`equip-unit-${u.id || idx}`}>
                    <div className="w-8 h-8 rounded-lg bg-[#E31937]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-[#E31937]">{idx + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{u.year} {u.make} {u.model}</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1 text-xs text-gray-500">
                        <span>Stock #{u.stock_number || '—'}</span>
                        <span>VIN: {u.vin || '—'}</span>
                        <span>Mileage: {u.mileage_km ? `${Number(u.mileage_km).toLocaleString()} km` : '—'}</span>
                        <span className="text-[#E31937] font-semibold">{u.price ? `$${Number(u.price).toLocaleString()}` : '—'}</span>
                      </div>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-gray-600 hover:text-[#E31937] hover:border-[#E31937]/30"
                        onClick={() => { setEditUnitData(u); setEditUnitMode('edit'); setPriceOverride(deal?.price_overrides?.[u.id]?.toString() || ''); setViewAllEquipOpen(false); setEditUnitOpen(true); }}
                        data-testid={`modal-edit-unit-${u.id || idx}`}
                      ><Pencil className="w-2.5 h-2.5 mr-1" />Edit</Button>
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-gray-600 hover:text-blue-600 hover:border-blue-300"
                        onClick={() => { setEditUnitData(u); setEditUnitMode('swap'); setSwapSearch(''); setPriceOverride(''); openEquipmentPicker(); setViewAllEquipOpen(false); setEditUnitOpen(true); }}
                        data-testid={`modal-swap-unit-${u.id || idx}`}
                      ><RefreshCw className="w-2.5 h-2.5 mr-1" />Swap</Button>
                      {(deal?.units?.length || 0) > 1 && (
                        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-red-400 hover:text-red-600 hover:border-red-300"
                          onClick={() => handleRemoveUnit(u.id)}
                          disabled={savingEquip}
                          data-testid={`modal-delete-unit-${u.id || idx}`}
                        ><Trash2 className="w-2.5 h-2.5 mr-1" />Delete</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Add more button */}
              <Button
                size="sm"
                variant="outline"
                className="w-full text-xs border-dashed border-gray-300 text-gray-600 hover:border-[#E31937] hover:text-[#E31937]"
                onClick={() => { setAddEquipMode(true); setAddEquipSearch(''); openEquipmentPicker(); }}
                data-testid="add-more-equipment-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Equipment
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Back button */}
              <Button size="sm" variant="ghost" className="text-xs text-gray-500 h-7" onClick={() => setAddEquipMode(false)} data-testid="back-to-equip-list">
                <ArrowLeft className="w-3 h-3 mr-1" /> Back to list
              </Button>
              {/* Search */}
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search available inventory..."
                  value={addEquipSearch}
                  onChange={e => setAddEquipSearch(e.target.value)}
                  className="pl-9 bg-gray-50 border-gray-200 text-sm"
                  data-testid="add-equip-search"
                />
              </div>
              {/* Inventory list */}
              <div className="max-h-[350px] overflow-y-auto space-y-1.5" data-testid="add-equip-inventory-list">
                {loadingInv ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                ) : filteredAddInventory.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No available units to add</p>
                ) : (
                  filteredAddInventory.map(unit => (
                    <div
                      key={unit.id}
                      className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 px-3 py-2.5 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-[#E31937]/20"
                      onClick={() => handleAddUnitToDeal(unit.id)}
                      data-testid={`add-inv-item-${unit.id}`}
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">{unit.year} {unit.make} {unit.model}</p>
                        <p className="text-xs text-gray-500">Stock #{unit.stock_number} &middot; {unit.mileage_km ? `${Number(unit.mileage_km).toLocaleString()} km` : '—'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#E31937]">{unit.price ? `$${Number(unit.price).toLocaleString()}` : '—'}</span>
                        <Plus className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Lightbox */}
      <Dialog open={lightbox.open} onOpenChange={(open) => { if (!open) { if (lightbox.url) window.URL.revokeObjectURL(lightbox.url); setLightbox({ open: false, url: '', filename: '', type: '' }); } }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-4xl max-h-[90vh] p-0" data-testid="doc-lightbox">
          <DialogHeader className="p-4 pb-2 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-sm font-medium truncate pr-4">{lightbox.filename}</DialogTitle>
              <Button size="sm" variant="outline" className="text-xs h-7 border-gray-300 flex-shrink-0" onClick={() => {
                const a = document.createElement('a');
                a.href = lightbox.url;
                a.download = lightbox.filename;
                a.click();
              }} data-testid="lightbox-download-btn">
                <Download className="w-3 h-3 mr-1" /> Download
              </Button>
            </div>
          </DialogHeader>
          <div className="p-4 flex items-center justify-center overflow-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
            {lightbox.type === 'image' ? (
              <img src={lightbox.url} alt={lightbox.filename} className="max-w-full max-h-[70vh] object-contain rounded" data-testid="lightbox-image" />
            ) : lightbox.type === 'pdf' ? (
              <iframe src={lightbox.url} title={lightbox.filename} className="w-full h-[70vh] border-0 rounded" data-testid="lightbox-pdf" />
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Preview not available for this file type</p>
                <p className="text-xs text-gray-400 mt-1">{lightbox.filename}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Document Generate Dialog */}
      <DocGenerateDialog
        open={docGenOpen}
        onClose={() => setDocGenOpen(false)}
        dealId={dealId}
        clientEmail={deal?.client?.email}
        clientName={deal?.client?.name}
        onSent={load}
      />

      {/* Send Document Email Dialog */}
      <Dialog open={sendEmailOpen} onOpenChange={(o) => { if (!o) { setSendEmailOpen(false); setSendEmailDoc(null); } }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md" data-testid="send-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              <Send className="w-4 h-4 text-blue-500" /> Send Document via Email
            </DialogTitle>
          </DialogHeader>
          {sendEmailDoc && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 text-sm">
                <p className="font-medium capitalize">{sendEmailDoc.type?.replace(/_/g, ' ')}</p>
                <p className="text-xs text-gray-500">{sendEmailDoc.filename}</p>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-600 uppercase">Send To</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded border border-gray-200 hover:bg-gray-50" data-testid="send-to-client">
                    <input type="radio" name="sendTo" checked={sendEmailMode === 'client'} onChange={() => setSendEmailMode('client')} className="accent-[#E31937]" />
                    <div>
                      <p className="font-medium text-gray-800">Client</p>
                      <p className="text-xs text-gray-400">{deal?.client?.email || 'No email'}</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded border border-gray-200 hover:bg-gray-50" data-testid="send-to-lender">
                    <input type="radio" name="sendTo" checked={sendEmailMode === 'lender'} onChange={() => setSendEmailMode('lender')} className="accent-[#E31937]" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">Lender</p>
                      {sendEmailMode === 'lender' && (
                        <Select value={sendEmailLenderId} onValueChange={setSendEmailLenderId}>
                          <SelectTrigger className="bg-gray-50 border-gray-200 mt-1 text-xs h-8" data-testid="send-lender-select"><SelectValue placeholder="Select lender..." /></SelectTrigger>
                          <SelectContent>
                            {lenders.map(l => <SelectItem key={l.id} value={l.id}>{l.name} {l.contact_email ? `(${l.contact_email})` : ''}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded border border-gray-200 hover:bg-gray-50" data-testid="send-to-custom">
                    <input type="radio" name="sendTo" checked={sendEmailMode === 'custom'} onChange={() => setSendEmailMode('custom')} className="accent-[#E31937]" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">Custom Email</p>
                      {sendEmailMode === 'custom' && (
                        <Input type="email" placeholder="recipient@example.com" value={sendEmailCustom} onChange={e => setSendEmailCustom(e.target.value)} className="bg-gray-50 border-gray-200 mt-1 text-xs h-8" data-testid="send-custom-email-input" />
                      )}
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" className="border-gray-300" onClick={() => setSendEmailOpen(false)}>Cancel</Button>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSendDocEmail} disabled={sendingEmail || (sendEmailMode === 'custom' && !sendEmailCustom) || (sendEmailMode === 'lender' && !sendEmailLenderId)} data-testid="confirm-send-email">
                  {sendingEmail ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending...</> : <><Send className="w-3 h-3 mr-1" /> Send Email</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Category Upload Dialog */}
      <Dialog open={docUploadOpen} onOpenChange={setDocUploadOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md max-h-[80vh] overflow-y-auto" data-testid="doc-category-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#E31937]" /> Categorize Documents
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-500">Select a document type for each file before uploading.</p>
            {pendingFiles.map((entry, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-3 border border-gray-200" data-testid={`pending-file-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-800 truncate flex-1">{entry.file.name}</span>
                  <span className="text-[10px] text-gray-400">{(entry.file.size / 1024).toFixed(0)} KB</span>
                </div>
                <Select value={entry.category} onValueChange={v => setPendingFiles(prev => prev.map((e, idx) => idx === i ? { ...e, category: v, customCategory: v === 'Other' ? e.customCategory : '' } : e))}>
                  <SelectTrigger className="h-8 text-xs bg-white border-gray-200" data-testid={`doc-cat-select-${i}`}>
                    <SelectValue placeholder="Select document type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {DOC_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {entry.category === 'Other' && (
                  <Input
                    placeholder="Enter category name..."
                    value={entry.customCategory}
                    onChange={e => setPendingFiles(prev => prev.map((en, idx) => idx === i ? { ...en, customCategory: e.target.value } : en))}
                    className="mt-2 h-8 text-xs bg-white border-gray-200"
                    data-testid={`doc-cat-custom-${i}`}
                  />
                )}
              </div>
            ))}
            <Button className="w-full bg-[#E31937] hover:bg-[#C4122C] text-white text-sm" onClick={handleConfirmUpload} disabled={uploadingDoc} data-testid="confirm-upload-btn">
              {uploadingDoc ? 'Uploading...' : `Upload ${pendingFiles.length} File(s)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <LenderPackagePreview open={lenderPkgPreviewOpen} onClose={() => setLenderPkgPreviewOpen(false)} dealId={dealId} />
    </div>
  );
}
